// The mother is the sum of her buildings plus her own books.
// One rule: money fields ADD, margins are RECOMPUTED from the summed earned/profit.
// Fractions 0-1, same convention as jcComputeProject's summary.
// Works unchanged for period figures because each member's period figure is already
// a diff of cumulatives — sums of diffs telescope exactly like the members do.
// Warnings never roll up: they mean something on a member, nothing on a sum.

// openEarned — what OPEN contract lines earned (no cap, so no contract of
// their own). It adds like earned does, and it has to ride every sum: "% of
// contract" (Ordinex.jobValue.earnedOfContract) takes it out of the numerator,
// and a roll-up that dropped it would put a mother back at 2,157%.
const NUM_FIELDS = ['directSpent', 'overheadSpent', 'totalSpent', 'earned', 'openEarned', 'contract', 'profit'];

// Every member's money is whole cents (jcComputeProject fits its costs, and
// job-value.js rounds contract and earned per line), so a sum of them is whole
// cents too. This only strips the float noise an addition leaves — 0.1 + 0.2 —
// which is what an export would otherwise print. It never moves a figure.
const cents = (v) => Math.round(v * 100) / 100;

export function sumSummaries(list) {
  const out = {};
  NUM_FIELDS.forEach(f => { out[f] = 0; });
  const perGroup = {};
  (list || []).forEach(s => {
    if (!s) return;
    NUM_FIELDS.forEach(f => { out[f] += Number(s[f]) || 0; });
    Object.keys(s.perGroup || {}).forEach(k => {
      perGroup[k] = (perGroup[k] || 0) + (Number(s.perGroup[k]) || 0);
    });
  });
  NUM_FIELDS.forEach(f => { out[f] = cents(out[f]); });
  Object.keys(perGroup).forEach(k => { perGroup[k] = cents(perGroup[k]); });
  out.perGroup = perGroup;
  out.marginPct = out.earned > 0 ? out.profit / out.earned : null;
  return out;
}

/** {parentId: [childRow…]} over rows carrying parent_id. */
export function childrenByParent(projects) {
  const out = {};
  (projects || []).forEach(p => {
    if (p && p.parent_id) (out[p.parent_id] = out[p.parent_id] || []).push(p);
  });
  return out;
}

// ── Shared root+children predicates (project-first nav, Task 1) ────────────
// Job Costs' per-project compute (jcComputeProject) is read by more than one
// screen now — the P&L portfolio and the admin home's job cards both need
// "what does this root project's row show, counting every sub-project's
// money in" and "does this root have a priced contract anywhere in it". Both
// screens do their OWN fetch+compute pass (their own jcFetchFeeds(null) +
// one jcComputeProject per project, cached under their own tab-local
// variable), but the ANSWER for a given pid/kids pair must be byte-identical
// between them — that is the money law this app runs on. So the math lives
// here, once, taking the caller's own computedById map ({projectId:
// jcComputeProject(...) result}) as a parameter instead of closing over any
// tab's private cache; each screen's own rollup helper becomes a one-line
// call-through to these (see 72-pnl.js's _pnlRollupJTD/_pnlContractRollup/
// _pnlRollupNoContract), so the two can never drift apart by definition.

const _ROLLUP_ZERO = { ...Object.fromEntries(NUM_FIELDS.map(f => [f, 0])), perGroup: {}, marginPct: null };

// Sum of job-to-date summaries — a root's own summary plus every child's,
// via sumSummaries (money fields ADD, marginPct is RECOMPUTED — see this
// file's own header). Degenerates to the root's own summary when `kids` is
// empty, so a leaf project's rollup is itself, byte-identical to reading
// `computedById[pid].summary` directly.
export function rollupSummary(computedById, pid, kids) {
  const own = (computedById[pid] && computedById[pid].summary) || _ROLLUP_ZERO;
  if (!kids || !kids.length) return own;
  return sumSummaries([own, ...kids.map(k =>
    (computedById[k.id] && computedById[k.id].summary) || _ROLLUP_ZERO)]);
}

// Contract is always the job-to-date sum, own + every child's — a reference
// value that never windows to a period (a period-figures object carries no
// `contract` key; see 72-pnl.js's own comment on this).
export function rollupContract(computedById, pid, kids) {
  return rollupSummary(computedById, pid, kids).contract || 0;
}

// Em-dash rule: a rollup has "no contract" only when NEITHER the root NOR
// any of its children has a priced contract line — one member priced is
// enough to make the combined Earned number mean something.
export function rollupNoContract(computedById, pid, kids) {
  const noneHas = id => ((computedById[id] && computedById[id].contractLines) || []).length === 0;
  return (kids && kids.length) ? [pid, ...kids.map(k => k.id)].every(noneHas) : noneHas(pid);
}

// The engine's own "does this job have revenue on file" flag (jcComputeProject's
// hasContract: contract > 0 OR earned > 0, job to date), rolled up as "any
// member has it". NOT the same question as rollupNoContract above: a job whose
// lines are all unpriced HAS lines and still has nothing to earn against. This
// is the predicate behind "is a negative profit here a loss, or just revenue
// nobody has typed yet" — Home and the P&L both read it, so they cannot drift.
export function rollupHasContract(computedById, pid, kids) {
  const has = id => !!(computedById[id] && computedById[id].hasContract);
  return has(pid) || (kids || []).some(k => has(k.id));
}

// Whether a mother's OWN books (as opposed to her sub-projects') carry any
// money — the one test behind her "— general" row on every screen that prints
// it. `contract` is always job to date; `figures` is whatever that screen is
// showing (job to date, or a period). A plain OR chain works because 0 is the
// only falsy number this data produces (a negative profit is still truthy).
export function hasOwnMoney(contract, figures) {
  const f = figures || {};
  return !!(Number(contract) || f.directSpent || f.overheadSpent || f.totalSpent || f.earned || f.profit);
}

// What a list of job rows says about losses, for the strips on Home and the
// P&L. `rows` are [{ hasContract, figures }] — figures as displayed (job to
// date or a period), hasContract always job to date (rollupHasContract).
//
//   losers          jobs WITH revenue on file whose profit is below zero
//   noContractJobs  jobs with cost and no revenue on file — not "at a loss":
//                   the revenue has simply not been typed yet
//   noContractCost  what those jobs have cost. It stays inside every total
//                   (Earned - Total cost = Profit keeps footing); this figure
//                   only lets the screen say how much of the total it is.
export function lossSummary(rows) {
  let losers = 0, noContractJobs = 0, noContractCost = 0;
  (rows || []).forEach(r => {
    const f = (r && r.figures) || {};
    const cost = Number(f.totalSpent) || 0;
    if (!r.hasContract) {
      if (Math.abs(cost) >= 0.005) { noContractJobs += 1; noContractCost += cost; }
    } else if ((Number(f.profit) || 0) < 0) losers += 1;
  });
  return { losers, noContractJobs, noContractCost };
}
